import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import DateFnsUtils from '@date-io/date-fns';
import { SnackbarProvider } from 'notistack';
import Zoom from '@material-ui/core/Zoom';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import {
  makeStyles,
  jssPreset,
  createStyles,
  ThemeProvider,
  StylesProvider
} from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useJsApiLoader } from '@react-google-maps/api';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  Auth,
  CacheProvider,
  SystemAlert,
  CookiesNotification,
  GoogleAnalytics,
  ScrollReset,
  PORTAL_TYPE,
  setNotificationSettings,
  setApplicationDetails,
  setAlertSettings,
  setUserData,
  GuestGuard,
  SettingsNotification,
  Routes,
} from '@nsd/fe';
import useDealers from './hooks/useDealers';
import accountService from './services/accountService';
import CompleteRegistrationView from './views/complete-register-view';
import ExternalLoginView from './views/ExternalClaims/ExternalLoginView';
import apps from 'src/apps';
import hasAccess, { GlassRoleNames, ROLES, isRoamingAdmin } from 'src/utils/hasAccess';
import useInitialTheme from 'src/hooks/useInitialTheme';
import { ThemeOptionsProvider } from 'src/context/ThemeOptionsContext';
import CreateExternalClaimWizard from './views/ExternalClaims/ExternalClaimsView';
import GlassOrderLandingPage from './views/ExternalClaims/BrowseClaimView/GlassOrderLandingPage';
const APP_NAME = 'Dealer Portal';
const APP_END_USER = PORTAL_TYPE.CLIENT;
/* Used to determine loading/splash screen logo
Choices for APP_INTERNAL_NAME
[
  "dispatch-qore",
  "lender-qore",
  "client-qore",
  "provider-qore",
  "claims-qore",
  "contact-qore",
  "insight-qore",
  "sales-qore",
  "marketing-qore",
  "market-qore",
  "developer-qore",
  "pricebook-qore",
  "underwriter-qore",
  "integration-qore",
  "admin-qore",
  "driver-qore",
  "billing-qore",
  "agent-qore",
  "dealer-qore",
  "licensing-qore",
  "ledger-qore",
  "data-qore",
  "notifications-qore"
]
*/
const APP_INTERNAL_NAME = 'dealer-qore';
const alertsConfigObject = {
  actionButtonContent: CancelPresentationIcon,
  // Used standard or filled
  variant: 'filled',
  position: 'top',
  transitionComponent: Zoom
};

const notificationsConfigObject = {
  pulseAnimation: true,
  displayNumberedBadge: true
};

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
const GOOGLE_SCRIPT_LIBRARIES = ['drawing,places'];
const useStyles = makeStyles((theme) =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%'
      },
      body: {
        height: '100%',
        width: '100%'
      },
      '#root': {
        height: '100%',
        width: '100%'
      },
      logo: {
        width: '100%',
        height: theme?.logo?.login?.height,
      },
    }
  })
);

const openSettingsWidget = () => {
  var node = document.querySelector('[title="Settings"]');
  node.click();
};

/**
 * @param {{dealerships: {dealerCode: string}[]}} newDealers 
 * @param {{active: boolean, dealerCode: string}[] | null} cachedDealers 
 * @returns {{dealerCode: string, active: boolean}[]}
 */
const updateUserAssignedDealers = (newDealers, cachedDealers) => {
  let activeDealerCode = newDealers.dealerships?.[0].dealerCode;

  if (cachedDealers != null) {
    const activeCachedDealer = cachedDealers.find(dealer => dealer.active);
    const matchingDealer = newDealers.dealerships.find(
      dealer => dealer.dealerCode === activeCachedDealer?.dealerCode
    );
    activeDealerCode = matchingDealer
      ? matchingDealer.dealerCode
      : activeDealerCode;
  }

  return newDealers.dealerships.reduce((acc, curr) => {
    const active = curr.dealerCode === activeDealerCode;
    return [...acc, { ...curr, active }];
  }, []);
}

function App() {
  // Redirect if current URL has glassclaim(-env).nationsafedrivers.com subdomain and is on root path
  useEffect(() => {
    const { hostname, pathname } = window.location;
    const glassclaimRegex = /^glassclaim(-dev|-stg|-uat|-demo)?\.nationsafedrivers\.com$/i;
    if (glassclaimRegex.test(hostname) && (pathname === '/' || pathname === '')) {
      window.location.replace('/glass/search');
    }
  }, []);

  const rootPages = [
    {
      exact: true,
      guard: GuestGuard,
      path: '/complete-registration',
      view: () => <CompleteRegistrationView />
    },
    {
      exact: true,
      // Removed GuestGuard to allow public access
      path: '/glass/search',
      view: () => <ExternalLoginView />
    },
    {
      exact: true,
      path: '/glass/claim',
      view: () => <CreateExternalClaimWizard />
    },
    {
      exact: true,
      guard: GuestGuard,
      path: '/glass/view-claim',
      view: () => <GlassOrderLandingPage />
    },
  ];
  const { areCachedDealersValid, getCachedDealers, setCachedDealers } = useDealers();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      setApplicationDetails(
        APP_END_USER,
        APP_NAME,
        null,
        APP_INTERNAL_NAME
      )
    );
  }, [dispatch]);
  // Set alert and notification preferences / settings
  useEffect(() => {
    dispatch(setAlertSettings(alertsConfigObject));
    dispatch(setNotificationSettings(notificationsConfigObject));
  }, [dispatch]);

  const { user, appSettings } = useSelector(state => state.account);
  const appType = useSelector(state => state.application?.type);

  let [hasLoadedInitialAgents, setHasLoadedInitialAgents] = useState(false);
  useEffect(() => {
    (async () => {
      if (user && !hasLoadedInitialAgents) {
        const isNsdAdmin = hasAccess(user.roles, ROLES.CLAIMS.NSD_ADMIN);
        const isSuperUser = isNsdAdmin || isRoamingAdmin(user.roles);
        //check if roles has role with name listed in GlassRoleNames
        const hasGlassRole = user?.roles?.some(role => GlassRoleNames.includes(role.name)) ?? false;

        const cachedDealers = getCachedDealers();
        const activeDealer = cachedDealers?.find(c => c.active);

        let newDealers;
        try {
          newDealers = await accountService.getDealerships(isSuperUser, 10, 0, '', activeDealer);
        } catch (err) {
          console.error('getDealerships error', err?.message);
          setHasLoadedInitialAgents(true);
          return;
        }

        const agents = areCachedDealersValid(cachedDealers, newDealers.dealerships)
          ? cachedDealers
          : updateUserAssignedDealers(newDealers, cachedDealers);

        setHasLoadedInitialAgents(true);

        setCachedDealers(agents);

        dispatch(
          setUserData(
            { ...user, agents, isSuperUser },
            appSettings,
            user.roles,
            null
          )
        );
        if (hasGlassRole) {
          dispatch(
            setApplicationDetails(
              APP_END_USER,
              'Glass Claims Portal',
              null,
              APP_INTERNAL_NAME
            )
          );
        }
      }
    })();
  }, [user]);

  // Pass in your customPath into hook
  // Make this your own custom path to decide where in firestore to listen
  const classes = useStyles();
  const { theme, allThemes } = useInitialTheme();
  // Will silently fail without a Google API key
  useJsApiLoader({
    id: 'google-maps',
    libraries: GOOGLE_SCRIPT_LIBRARIES,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY
  });


  if (!theme || !appType) {
    return null;
  }
  return (
    <CacheProvider>
      <DndProvider backend={HTML5Backend}>
        <ThemeProvider theme={theme}>
          <ThemeOptionsProvider allThemes={allThemes}>
            <StylesProvider jss={jss}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <SnackbarProvider maxSnack={1}>
                  <Router history={history}>
                    <SystemAlert />
                    <Auth>
                      <ScrollReset />
                      <GoogleAnalytics />
                      <CookiesNotification />
                      <SettingsNotification
                        settingsPathFunc={openSettingsWidget}
                      />
                      <Routes
                        type={appType}
                        additionalRootPages={rootPages}
                        apps={apps || []}
                        logoConfig={{
                          logo: theme.logo?.login?.src,
                          logoAlt: 'Login Logo',
                          height: theme.logo?.login?.height,
                          className: classes.logo,
                        }}
                      />
                    </Auth>
                  </Router>
                </SnackbarProvider>
              </MuiPickersUtilsProvider>
            </StylesProvider>
          </ThemeOptionsProvider>
        </ThemeProvider>
      </DndProvider>
    </CacheProvider>
  );
}

export default App;
